/* eslint-disable no-useless-escape */
import { getUserAgent, getLocation, getNavigatorLang } from './ssrCompatible'
import { REGION_LANG_MAP, LocaleList } from '@/consts/index'
import { globalVariable } from '@/utils/exportVariable'
import { getCookie } from '@/utils/tool'
import { isLocalIp } from '@/utils/env'
import { ROUTE_FIX } from '@/config/routePrefix'
import { countrysList, getDefaultCountry } from '@/config/country'
import { landScapeRouterWhiteList } from '@/consts/index'
import { isMsaWebView } from '@/utils/msa'
import { isHorizontalWithInner } from '@/utils/platform'
import { RENDER_TEMP } from '@page_detail/config'

const UaParser = require('ua-parser-js')

// 白名单机型正则
const cert_white_list = [
  'M2102J20SG', // 红米POCO X3  Pro
  'M2003J15SC', // 红米note 9
]
const certWhiteListRegex = cert_white_list.map((item) => `(${item})`).join('|')

export const isInCoinsApp = () => {
  return /getcoins/i.test(getUserAgent())
}

// 云原神新增 GetApps
export const isInGetApp = () => {
  return /mipicks/i.test(getUserAgent())
}

/**
 * 是否为小米浏览器国际版
 */
export function isMiuiBrowser() {
  const UA = getUserAgent()
  const uaParser = new UaParser(UA)
  const { name = '' } = uaParser.getBrowser() || {}
  return (
    /XiaoMi\/MiuiBrowser\/.*(gn|g)/.test(UA) &&
    name.toLocaleLowerCase().indexOf('miui') > -1
  ) // eslint-disable-line
}

export function isXiaomiDevice() {
  const uaParser = new UaParser(getUserAgent())
  const { vendor = '' } = uaParser.getDevice() || {}
  return vendor.toLocaleLowerCase().indexOf('xiaomi') > -1
}

export function isXiaomiDeviceForPopup() {
  const uaParser = new UaParser(getUserAgent())
  const ua = (uaParser.getUA() || '').toLocaleLowerCase()
  return /(mi)|(redmi)|(xiaomi)/i.test(ua) || isXiaomiDeviceByCert(ua)
}

export function getBrowserType(isMiuiBrowser, isChromeBrowser) {
  // 判断是那种浏览器 - 用户gt适配
  if (isMiuiBrowser) {
    return 'xiaomi'
  }
  if (isChromeBrowser) {
    return 'chrome'
  }
  return 'other'
}

// 某些机型ua会由cert值代替品牌名
function isXiaomiDeviceByCert(ua) {
  return new RegExp(certWhiteListRegex, 'i').test(ua)
}

// 是否是ios
export function isIosDevice() {
  const uaParser = new UaParser(getUserAgent())
  const ua = (uaParser.getUA() || '').toLocaleLowerCase()
  return /(iPhone|iPad|iPod|iOS)/i.test(ua)
}

/**
 * 是否是chrome浏览器
 */
export function isChromeBrowser() {
  // const agent = window.navigator.userAgent.toLowerCase()
  // return /chrome/.test(agent)
  const uaParser = new UaParser(getUserAgent())
  const { name = '' } = uaParser.getBrowser() || {}
  return name.toLocaleLowerCase().indexOf('chrome') > -1
}

export function getDomain() {
  if (!process.client) {
    return ''
  }
  const { hostname, host = '' } = window.location || {}
  if (hostname === 'localhost' || isLocalIp()) {
    return hostname
  }
  return '.' + host.split('.').slice(-2).join('.')
}

export function isPwa() {
  if (!process.client) {
    const search = getLocation().search
    return search.indexOf('isPwa=true') > -1
  }

  const search = window.location.search
  return (
    search.indexOf('isPwa=true') > -1 ||
    window.matchMedia('(display-mode: standalone)').matches
  )
}

// 节流
export function throttle(fn, delay) {
  var timer
  return function () {
    var _this = this
    var args = arguments
    if (timer) {
      return
    }
    timer = setTimeout(function () {
      fn.apply(_this, args)
      timer = null // 在delay后执行完fn之后清空timer，此时timer为假，throttle触发可以进入计时器
    }, delay)
  }
}

export function getQuery(name) {
  const { context } = globalVariable

  if (!name || (process.server && !context?.req?.url)) {
    return ''
  }

  try {
    const search = getLocation().search

    const searchStr =
      (() => {
        const res = (search && search.split('?')) || []
        if (res.length > 1) {
          return res[1]
        }
      })() || ''

    const searchMap = {}
    searchStr.split('&').forEach((item) => {
      searchMap[item.split('=')[0]] = item.split('=')[1]
    })
    return searchMap[name]
  } catch (e) {
    return ''
  }
}

/**
 * 用途：获得多个查询参数构成的对象
 * @param {Arrray of String } 目标参数数组
 * @returns 查询参数构成的对象
 */
export function getQueriesObject(arr) {
  let res = {}
  if (Array.isArray(arr) && arr.length > 0) {
    res = arr.reduce((prev, curr) => {
      const queryVal = getQuery(String(curr))
      return queryVal ? { ...prev, [curr]: queryVal } : prev
    }, {})
  }
  return res
}

function findRegionFromPathname(pathname, showLog) {
  // pathname 是 /br/xx
  const lastIndex = pathname.lastIndexOf('/') >= 3
  /* eslint-disable */
  showLog && console.log('lastIndex ===>>>', lastIndex, pathname)
  if (lastIndex) {
    return `${pathname.slice(1, 3)}`
  } else {
    const result = ROUTE_FIX.some((item) => pathname === item)
    /* eslint-disable */
    showLog && console.log('result ===>>>', result, pathname)
    if (result) {
      return pathname.slice(1)
    }
    return null
  }
}

function getRouterBasePrefix() {
  const rawBase = process.env.VUE_APP_ROUTER_BASE || '/'
  if (rawBase === '/') return ''
  const base = rawBase.startsWith('/') ? rawBase : `/${rawBase}`
  return base.endsWith('/') ? base.slice(0, -1) : base
}

function stripRouterBase(pathname = '') {
  const routerBasePrefix = getRouterBasePrefix()
  if (!routerBasePrefix || routerBasePrefix === '/') return pathname
  if (pathname === routerBasePrefix) return '/'
  if (pathname.startsWith(`${routerBasePrefix}/`)) {
    return pathname.slice(routerBasePrefix.length) || '/'
  }
  return pathname
}

// 判断线上项目所在地区
export function region(locations = {}, showLog) {
  const defaultCountry = getDefaultCountry()
  const { host, pathname: rawPathname } = getLocation(locations, showLog)
  const pathname = stripRouterBase(rawPathname)
  const countryMaps = countrysList()

  /* eslint-disable */
  showLog && console.log('region() host ===>>>', host, pathname)

  if (host.indexOf('www') !== -1 && pathname === '/') return defaultCountry

  const _path = pathname.slice(1).split('/')[0]
  // todo 新增加路由前缀的方式区分语言，
  if (_path && ROUTE_FIX.includes(`/${_path}`)) {
    return findRegionFromPathname(pathname, showLog) || defaultCountry
  }

  const urlReg = new RegExp(/[a-zA-Z0-9]+|\-|[a-zA-Z0-9]/g)
  const urlResults = host.match(urlReg) || []
  return countryMaps.includes(urlResults[0]) ? urlResults[0] : defaultCountry
}

function targetLang(locale) {
  return LocaleList.find((item) => item.includes(locale))?.replace('-', '_')
}

/* 全局语言初始化 */
export function locale() {
  const regionDomain = region() && region().toUpperCase()
  const localeLangArr = ((l) => (l ? l.split('_') : null))(
    getCookie('localeLang')
  ) // 缓存的国家语言一定是语料库和 REGION_LANG_MAP 包含的，但是有可能人为随意修改了缓存数据
  const cacheLang = localeLangArr ? localeLangArr[0] : '' // 语言简称，小写
  const cacheRegion = localeLangArr ? localeLangArr[1] : '' // 国家简称，大写

  const deviceLocale = targetLang(getNavigatorLang())
  const deviceLocaleArr = ((l) => (l ? l.split('_') : null))(deviceLocale)
  const deviceLang = deviceLocaleArr ? deviceLocaleArr[0] : '' // 语言简称，小写
  const deviceRegion = deviceLocaleArr ? deviceLocaleArr[1] : '' // 国家简称，大写

  let result = targetLang(regionDomain) || 'en_US' // 兜底，取当前域名的默认语言

  // 语言设置优先级：缓存 > 浏览器检测语言 > 当前域名默认语言 > 兜底语言
  if (
    localeLangArr &&
    regionDomain === cacheRegion &&
    REGION_LANG_MAP[cacheRegion] && // 防止人为修改无法被识别
    REGION_LANG_MAP[cacheRegion].children[cacheLang] // 防止人为修改无法被识别
    // REGION_LANG_MAP[cacheRegion]?.langs.indexOf(cacheLang) > -1 //上一行判断的简洁写法
  ) {
    result = getCookie('localeLang')
  } else if (
    deviceLocaleArr &&
    deviceRegion === regionDomain &&
    REGION_LANG_MAP[deviceRegion] &&
    REGION_LANG_MAP[deviceRegion].children[deviceLang]
  ) {
    result = deviceLocale
  } else if (REGION_LANG_MAP[regionDomain]) {
    result = `${REGION_LANG_MAP[regionDomain].langs[0]}_${regionDomain}`
  }
  // 当前域名对应的国家在 REGION_LANG_MAP 中也许没有被包含进去（可能出于某种原因没上语言切换功能）
  return result
}

// 跟踪记录用户路由切换与页面跳转
export function trackPath(fromPath, toPath) {
  if (!process.client) return

  sessionStorage.setItem('fromPath', fromPath)
  sessionStorage.setItem('toPath', toPath)

  let prevRef = sessionStorage.getItem('prevRef') || ''
  let currRef = document.referrer
  // document.referrer 发生变化(页面刷新会指向当前页面的地址)
  const gameRouteList = ['/lordsmobile', '/pubgm']
  if (prevRef !== currRef) {
    prevRef = currRef
    sessionStorage.setItem('fromPath', currRef) // 记录上一页
    sessionStorage.setItem('prevRef', prevRef)
    const lastViewedPageReg =
      /^http(s?):\/\/([a-zA-Z-]*\.)?((pagsmile.com)|(smile.one))/
    if (lastViewedPageReg.test(currRef) || fromPath === '/pay-result') {
      sessionStorage.setItem('isFromPayPage', true)
    }
  } else if (
    prevRef === currRef &&
    (fromPath === '/pay-result' ||
      (fromPath === '/order-detail' && gameRouteList.includes(toPath)))
  ) {
    // 当前 pubgm 与 Lordsmobile 都改为支付完成后跳转到订单详情页
    sessionStorage.setItem('isFromPayPage', true)
  } else {
    sessionStorage.setItem('isFromPayPage', false)
  }
  // 退出游戏详情页
  if (fromPath !== '/' && toPath === '/') {
    sessionStorage.setItem('isFromPayPage', false)
  }
}

/**
 * 获取指定字符串出现的次数
 * @param {*} str ：源字符串
 * @param {*} subString ：子传
 * @param {*} times：默认出现的次数
 * @returns
 */
export function getStringTimes(str, subString, times = 0) {
  if (str.indexOf(subString) > -1) {
    times += 1
    const endLength = str.indexOf(subString) + subString.length
    return getStringTimes(str.slice(endLength), subString, times)
  } else {
    return times
  }
}

// getBoundingClientRect 兼容方法
export function _getBoundingClientRect(el) {
  if (
    el &&
    el.getBoundingClientRect &&
    typeof el.getBoundingClientRect === 'function'
  ) {
    return el.getBoundingClientRect()
  }
  return {
    bottom: 0,
    height: 0,
    left: 0,
    right: 0,
    top: 0,
    width: 0,
    x: 0,
    y: 0,
  }
}

export function getCurrentPage(path) {
  const lastSlashIndex = path.lastIndexOf('/')
  let pageName = 'home'
  if (lastSlashIndex >= 3) {
    // 必然带了国家前缀，如'/ph/'或'/ph/somepage'
    pageName = path.slice(lastSlashIndex + 1) || 'home'
  } else {
    // 有可能是'/'或者'/ph' 或者'/somepage'
    if (path !== '/' && ROUTE_FIX.indexOf(path) === -1) {
      pageName = path.slice(lastSlashIndex + 1)
    }
  }
  return pageName
}

// 横屏引导展示的判断
export function isShowLandscapeGuide(state) {
  const { gameName, isInPubgmGameApp, isDesktop, isTurbo, region, isApp } =
    state
  // pc、gt、APP不展示
  if (isDesktop || isTurbo || isApp) {
    return false
  }
  // pubgm的webview不展示
  if (gameName === 'pubgm' && !!isInPubgmGameApp) {
    return false
  }
  // mobile的msa webview不展示
  if (gameName === 'mobile-legends' && !!isMsaWebView()) {
    return false
  }
  // 某路由不展示，例如：id mobile-legends详情页不展示(本期不做单独路由判断)
  if (
    landScapeRouterWhiteList &&
    Object.keys(landScapeRouterWhiteList).length
  ) {
    let path = getLocation()?.pathname || ''
    if (
      path &&
      landScapeRouterWhiteList[region] &&
      landScapeRouterWhiteList[region].length &&
      landScapeRouterWhiteList[region].includes(getCurrentPage(path))
    ) {
      return false
    }
  }
  // 其余根据真实情况判断：横屏展示竖屏不展示
  return !!isHorizontalWithInner()
}

// 获取页面中所有input框并失焦
export function allInputBlur() {
  let nodes = document?.getElementsByTagName('input')
  let textNodes = document?.getElementsByTagName('textarea')
  if (nodes.length) {
    for (let i = 0; i < nodes.length; i++) {
      nodes[i]?.blur()
    }
  }
  if (textNodes.length) {
    for (let i = 0; i < textNodes.length; i++) {
      textNodes[i]?.blur()
    }
  }
}

/**
 * 判断是否是奇数
 * 返回值：true 奇数 false偶数
 * @param {*} data
 */
export function checkOdd(data) {
  const number = (data + '').slice(-1) * 1
  return number % 2 === 1
}

// 游戏名称、转化为route路径名称
export const transformGamePath = () => {
  return RENDER_TEMP.map((item) => `/${item}`)
}

// 默认是英文的游戏
export const hackGameDefaultLanguage = (c, l) => {
  const gamesList = ['shining-nikki', 'genshin-en']

  const game = c.params?.gameName || c.route.params?.gameName || null

  if (c.route.name !== 'Detail2') return l

  return game && gamesList.includes(game) ? 'en_US' : l
}

export const jumpToPage = (url, type, target = '_self') => {
  if (!url) {
    return
  }
  if (process.client) {
    const { origin, pathname, search } = new URL(url)
    const currentOrigin = window.location.origin
    if (origin && origin.indexOf('.toplupe.') > -1 && origin.indexOf('pay.toplupe.') === -1) {
      // 包含.toplupe.且不是收银台链接
      const newUrl = `${currentOrigin}${pathname}${search}`
      if (type === 'open') {
        window.open(newUrl, target)
      } else {
        window.location.href = newUrl
      }
    } else {
      if (type === 'open') {
        window.open(url, target)
      } else {
        window.location.href = url
      }
    }
  }
}
